import {Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {of} from 'rxjs';

import {MaterialModule} from '../../../material.module';
import {ClientService} from '../../../services/auth/client.service';
import {DnbService} from '../../../services/enrich/dnb.service';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich/enrich-console-diagnostics.service';
import {EnrichReadiness, EnrichReadinessDimensionKey, EnrichSetupService} from '../../../services/enrich/enrich-setup.service';
import {
  EnrichSettings,
  HubspotSubUnitPreviewIssueCode,
  HubspotSubUnitPreviewResponse,
  HubspotSubUnitPreviewRow,
} from '../../../models/hubspot.model';
import {DnbUsageCardComponent} from '../settings/dnb-usage-card/dnb-usage-card.component';
import {EnrichSettingsComponent} from '../settings/settings.component';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ConnectConnectionsService, ConnectOAuthAuthorizationRequest} from '../../../services/connect/connect-connections.service';
import {ConnectOAuthPopupService} from '../../../services/connect/connect-oauth-popup.service';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {HubspotAuthService} from '../../../services/enrich/hubspot-auth.service';
import {ApiService} from '../../../services/enrich/api.service';
import {ReferenceService} from '../../../services/enrich/reference.service';
import {RegistryService} from '../../../services/enrich/registry.service';

type FixtureState =
  | 'completed'
  | 'partial'
  | 'broken-auth'
  | 'missing-scope'
  | 'missing-mapping'
  | 'unknown-permissions'
  | 'subunit-config-empty'
  | 'subunit-loading'
  | 'subunit-empty'
  | 'subunit-summary'
  | 'subunit-error'
  | 'dnb-usage'
  | 'company-capture'
  | 'company-capture-permissions';

// Only rendered by the existing localhost-only fixture route.
const capturePermissionsFixture = {
  granted: false,
  settings: {
    id: 'fixture-settings', clientId: 'fixture-client', portalId: 123456,
    hubspotConnectionId: 'fixture-hubspot', companyCaptureEnabled: false,
    subscriptionActive: false, syncOnCompanyCreated: false, existingCompanySyncFrequency: 'off',
    addCompaniesMatchingFilters: false, dataProvider: 'brreg', dataProviders: ['brreg'], dataSources: [],
    crmOrgNumberProperty: 'orgnr', crmSubUnitNumberProperty: '', filters: [], mapping: [],
    lastUpdateTimestamp: '2026-11-03T12:00:00.000Z',
  } as EnrichSettings,
};

const dnbUsageFixture = {
  enabled: true,
  checkedAt: '2026-08-29T14:00:00.000Z',
  provider: {
    status: 'ready' as const,
    lastMeterUpdatedAt: '2026-08-29T13:56:17.104Z',
    reportedRum: 2,
    limit: 550,
    meters: [{productName: 'D&B for Enterprise Master Data', region: 'Region 2', limit: 550, currentUsage: 2}],
  },
  shine: {status: 'ready' as const, observedCompanies: 2, paidReadsToday: 4, consumedReadsToday: 4, dailyLimit: 100},
  comparison: 'aligned' as const,
  watchlistEnabled: false as const,
};

@Component({
  selector: 'app-enrich-visual-fixture',
  standalone: true,
  imports: [FormsModule, MaterialModule, RouterLink, DnbUsageCardComponent, EnrichSettingsComponent],
  providers: [
    HubspotAuthService,
    {provide: ClientService, useValue: {client: {id: 'fixture-client'}}},
    {provide: DnbService, useValue: {getUsageStatus: () => of(dnbUsageFixture)}},
    {provide: EnrichConsoleDiagnosticsService, useValue: {reportError: () => undefined}},
    {provide: ClientLoadingService, useValue: {ensureClientSelected: () => of({id: 'fixture-client'})}},
    {provide: HubspotService, useValue: {
      getSettings: () => of({...capturePermissionsFixture.settings}),
      updateSettings: (settings: Partial<EnrichSettings>) => {
        capturePermissionsFixture.settings = {...capturePermissionsFixture.settings, ...settings};
        return of({...capturePermissionsFixture.settings});
      },
      setup: () => of({}), syncAllCompanies: () => of({}),
    }},
    {provide: ApiService, useValue: {get: () => of({}), post: () => of({})}},
    {provide: ReferenceService, useValue: {getHubspotProperties: () => of([
      {name: 'orgnr', label: 'Organization number', type: 'string', fieldType: 'text'},
    ])}},
    {provide: RegistryService, useValue: {getStatus: () => of({providers: []})}},
    {provide: ConnectConnectionsService, useValue: {
      list: () => of([{id: 'fixture-hubspot', name: 'Fixture HubSpot', ref: 'hubspot.oauth', connected: true}]),
      get: () => of({id: 'fixture-hubspot', name: 'Fixture HubSpot', ref: 'hubspot.oauth', connected: true}),
      authorize: (request: ConnectOAuthAuthorizationRequest) => {
        if (new URL(window.location.href).searchParams.get('captureConsent') === 'cancel') throw new Error('Consent cancelled');
        capturePermissionsFixture.granted = request.extra_scopes?.includes('crm.objects.contacts.read') === true;
        return of({url: 'https://example.invalid/fixture-consent'});
      },
      getPermissions: () => of({
        connectionId: 'fixture-hubspot', providerRef: 'hubspot.oauth', connected: true,
        hasRequiredScopes: capturePermissionsFixture.granted, source: 'metadata.oauthPermissions.grantedScopes',
        grantedScopes: capturePermissionsFixture.granted ? ['crm.objects.contacts.read'] : ['crm.objects.companies.read'],
      }),
    }},
    {provide: ConnectOAuthPopupService, useValue: {
      authorizeWith: async (start: (nonce: string) => Promise<{url: string}>) => {await start('fixture-nonce');},
    }},
  ],
  templateUrl: './enrich-visual-fixture.component.html',
  styleUrls: ['./enrich-visual-fixture.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
})
export class EnrichVisualFixtureComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly setupService = inject(EnrichSetupService);

  state: FixtureState = 'completed';
  readiness: EnrichReadiness = this.setupService.buildReadiness(this.settingsForState('completed'), {
    selectedConnectionId: 'fixture-hubspot',
    connectionPermissions: {connected: true, hasRequiredScopes: true},
  });
  subUnitPreview: HubspotSubUnitPreviewResponse | null = null;
  subUnitPreviewError: string | null = null;
  companyCaptureSelectionRequired = true;

  ngOnInit(): void {
    const requested = String(this.route.snapshot.queryParamMap.get('enrichVisualFixture') || 'completed') as FixtureState;
    this.state = requested;

    if (this.router.url.startsWith('/enrich/auth')) {
      this.router.navigate(['/enrich/setup'], {
        queryParams: {
          enrichVisualFixture: this.state,
        },
        replaceUrl: true,
      });
      return;
    }

    this.readiness = this.readinessForState(this.state);
    this.subUnitPreview = this.subUnitPreviewForState(this.state);
    this.subUnitPreviewError = this.state === 'subunit-error'
      ? 'Preview unavailable: CRM preview read failed for this page.'
      : null;
  }

  get routeLabel(): string {
    const path = this.router.url.split('?')[0];
    if (path.includes('/enrich/settings')) {
      return this.isSubUnitPreviewState ? 'Settings / Operations' : 'Settings';
    }
    if (path.includes('/enrich/mapping')) return 'Field Mapping';
    return 'Setup';
  }

  get readinessDimensions() {
    return this.readiness.dimensions;
  }

  get isSubUnitPreviewState(): boolean {
    return this.state.startsWith('subunit-');
  }

  get isSettingsOperationsFixture(): boolean {
    return this.isSubUnitPreviewState || this.state === 'dnb-usage' || this.state === 'company-capture';
  }

  get isCompanyCaptureFixture(): boolean { return this.state === 'company-capture'; }

  get isCompanyCapturePermissionsFixture(): boolean { return this.state === 'company-capture-permissions'; }

  get showReadinessFixture(): boolean {
    return !this.isSettingsOperationsFixture && !this.isCompanyCapturePermissionsFixture;
  }

  get showSubUnitPreviewFixture(): boolean {
    return this.isSettingsOperationsFixture;
  }

  get subUnitPreviewRows(): HubspotSubUnitPreviewRow[] {
    return this.subUnitPreview?.rows || [];
  }

  get subUnitPreviewReloadDisabled(): boolean {
    return this.state === 'subunit-loading';
  }

  get subUnitPreviewNextDisabled(): boolean {
    return !this.subUnitPreview?.nextAfter || this.subUnitPreview?.hasMore !== true || this.state === 'subunit-loading';
  }

  get subUnitPreviewConfigMessage(): string {
    return 'Select the BRREG provider and configure a HubSpot sub-unit property before loading the preview.';
  }

  get subUnitPreviewImpactText(): string {
    const propertyName = this.settingsForState(this.state).crmSubUnitNumberProperty || 'your sub-unit property';
    return `Preview the next bounded page of HubSpot companies with ${propertyName} before any sync job runs.`;
  }

  readinessIcon(key: EnrichReadinessDimensionKey): string {
    const status = this.readiness.dimensions.find(item => item.key === key)?.status;
    if (status === 'ready') return 'check_circle';
    if (status === 'unknown') return 'help';
    return 'error';
  }

  readinessStatusLabel(key: EnrichReadinessDimensionKey): string {
    const status = this.readiness.dimensions.find(item => item.key === key)?.status;
    if (status === 'ready') return 'Ready';
    if (status === 'unknown') return 'Unknown';
    return 'Action needed';
  }

  previewRegistryStatusLabel(row: HubspotSubUnitPreviewRow): string {
    if (row.registryStatus === 'matched') return 'Registry match';
    if (row.registryStatus === 'not_found') return 'Registry not found';
    return 'Registry error';
  }

  previewIssueLabel(issueCode: HubspotSubUnitPreviewIssueCode): string {
    if (issueCode === 'duplicate') return 'Duplicate';
    return 'Conflict';
  }

  previewPropertyList(propertyNames: string[]): string {
    return propertyNames.join(', ');
  }

  private readinessForState(state: FixtureState): EnrichReadiness {
    const settings = this.settingsForState(state);
    const selectedConnectionId = 'fixture-hubspot';

    if (state === 'broken-auth') {
      return this.setupService.buildReadiness(settings, {
        selectedConnectionId,
        authStatus: {authenticated: false},
        connectionPermissions: {connected: false, hasRequiredScopes: true},
      });
    }

    if (state === 'missing-scope') {
      return this.setupService.buildReadiness(settings, {
        selectedConnectionId,
        connectionPermissions: {
          connected: true,
          hasRequiredScopes: false,
          missingScopes: ['crm.objects.companies.write'],
        },
      });
    }

    if (state === 'unknown-permissions') {
      return this.setupService.buildReadiness(settings, {
        selectedConnectionId,
      });
    }

    return this.setupService.buildReadiness(settings, {
      selectedConnectionId,
      connectionPermissions: {connected: true, hasRequiredScopes: true},
    });
  }

  private settingsForState(state: FixtureState): EnrichSettings {
    const setupStatus = {
      authenticated: true,
      settingsCreated: true,
      webhooksSetup: true,
      propertiesCreated: true,
      mappingSetup: true,
      setupComplete: true,
    };

    if (state === 'partial') {
      setupStatus.settingsCreated = false;
      setupStatus.webhooksSetup = false;
      setupStatus.propertiesCreated = false;
      setupStatus.mappingSetup = false;
      setupStatus.setupComplete = false;
    }

    if (state === 'missing-mapping') {
      setupStatus.mappingSetup = false;
      setupStatus.setupComplete = false;
    }

    const isSubUnitPreviewReady = state !== 'subunit-config-empty';

    return {
      id: 'fixture-settings',
      clientId: 'fixture-client',
      subscriptionActive: true,
      syncOnCompanyCreated: true,
      existingCompanySyncFrequency: 'weekly',
      lastUpdateTimestamp: '2026-06-11T00:00:00.000Z',
      addCompaniesMatchingFilters: false,
      filters: [],
      mapping: setupStatus.mappingSetup ? [{sourceField: 'organization_number', targetField: 'org_number'}] : [],
      portalId: 123456,
      hubspotConnectionId: 'fixture-hubspot',
      crmOrgNumberProperty: setupStatus.settingsCreated ? 'organization_number' : '',
      crmSubUnitNumberProperty: isSubUnitPreviewReady ? 'subunit_number' : '',
      dataProviders: isSubUnitPreviewReady ? ['brreg'] : [],
      setupStatus,
    };
  }

  private subUnitPreviewForState(state: FixtureState): HubspotSubUnitPreviewResponse | null {
    const generatedAt = '2026-06-23T12:00:00.000Z';

    if (state === 'subunit-loading' || state === 'subunit-config-empty' || state === 'subunit-error') {
      return null;
    }

    if (state === 'subunit-empty') {
      return {
        schemaVersion: 'hubspot-subunit-preview.v1',
        generatedAt,
        clientId: 'fixture-client',
        configurationState: 'ready',
        configurationIssues: [],
        limit: 25,
        after: null,
        nextAfter: null,
        returnedRows: 0,
        hasMore: false,
        truncated: false,
        summary: {
          totalRows: 0,
          matchedRows: 0,
          wouldCreateRows: 0,
          wouldUpdateRows: 0,
          noOpRows: 0,
          duplicateRows: 0,
          conflictRows: 0,
          notFoundRows: 0,
          errorRows: 0,
        },
        rows: [],
      };
    }

    return {
      schemaVersion: 'hubspot-subunit-preview.v1',
      generatedAt,
      clientId: 'fixture-client',
      configurationState: 'ready',
      configurationIssues: [],
      limit: 25,
      after: null,
      nextAfter: 'company-004',
      returnedRows: 4,
      hasMore: true,
      truncated: false,
      summary: {
        totalRows: 4,
        matchedRows: 3,
        wouldCreateRows: 1,
        wouldUpdateRows: 1,
        noOpRows: 1,
        duplicateRows: 1,
        conflictRows: 1,
        notFoundRows: 1,
        errorRows: 0,
      },
      rows: [
        {
          companyId: 'company-001',
          companyName: 'Nordfjord Verksted AS',
          companyDomain: 'nordfjord.no',
          subUnitId: '912345678',
          orgNumber: '912300001',
          registryStatus: 'matched',
          outcome: 'would-update',
          issueCodes: [],
          registryCompanyName: 'Nordfjord Verksted Avdeling Sentrum',
          parentOrgNumber: '912300001',
          createPropertyNames: [],
          updatePropertyNames: ['company_name', 'address', 'postal_code'],
          diffPropertyCount: 3,
          errorMessage: null,
        },
        {
          companyId: 'company-002',
          companyName: 'Vestbygg Gruppen',
          companyDomain: 'vestbygg.no',
          subUnitId: '923456789',
          orgNumber: '923400002',
          registryStatus: 'matched',
          outcome: 'would-create',
          issueCodes: ['duplicate'],
          registryCompanyName: 'Vestbygg Gruppen Avd Bergen',
          parentOrgNumber: '923400002',
          createPropertyNames: ['company_name', 'street_address', 'city'],
          updatePropertyNames: [],
          diffPropertyCount: 3,
          errorMessage: null,
        },
        {
          companyId: 'company-003',
          companyName: 'Polar Drift Driftssenter',
          companyDomain: 'polardrift.no',
          subUnitId: '934567890',
          orgNumber: '999999999',
          registryStatus: 'matched',
          outcome: 'no-op',
          issueCodes: ['conflict'],
          registryCompanyName: 'Polar Drift Driftssenter Tromso',
          parentOrgNumber: '934500003',
          createPropertyNames: [],
          updatePropertyNames: [],
          diffPropertyCount: 0,
          errorMessage: null,
        },
        {
          companyId: 'company-004',
          companyName: 'Arctic Retail Outlet',
          companyDomain: 'arcticretail.no',
          subUnitId: '945678901',
          orgNumber: '945600004',
          registryStatus: 'not_found',
          outcome: null,
          issueCodes: [],
          registryCompanyName: null,
          parentOrgNumber: null,
          createPropertyNames: [],
          updatePropertyNames: [],
          diffPropertyCount: 0,
          errorMessage: null,
        },
      ],
    };
  }
}
